import {
  isoDate,
  listingWallClockToIso,
  localDateIso,
  localToday,
  minutesOfDay,
  shiftIsoDate,
  wallClockExistsInZone,
  zoneAbbrev,
} from "~/lib/availability/setup";

// ---------------------------------------------------------------------------
// Core ISO helpers
// ---------------------------------------------------------------------------

export function pad(n: number) {
  return String(n).padStart(2, "0");
}

export function toLocalISO(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function parseLocalISO(value: string): Date | null {
  if (!value) return null;
  const [datePart, timePart] = value.split("T");
  if (!datePart || !timePart) return null;
  const dateParts = datePart.split("-").map(Number);
  const timeParts = timePart.split(":").map(Number);
  if (dateParts.length !== 3 || timeParts.length !== 2) return null;
  const [y, mo, d] = dateParts;
  const [h, m] = timeParts;
  if (
    y === undefined ||
    mo === undefined ||
    d === undefined ||
    h === undefined ||
    m === undefined
  )
    return null;
  if ([y, mo, d, h, m].some((n) => Number.isNaN(n))) return null;
  return new Date(y, mo - 1, d, h, m);
}

// ---------------------------------------------------------------------------
// Display formatting
// ---------------------------------------------------------------------------

export const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export function formatDisplay(value: string, now: Date): string {
  const d = parseLocalISO(value);
  if (!d) return "Select time";
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const isTomorrow =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate() + 1;
  const rawH = d.getHours();
  const period = rawH >= 12 ? "PM" : "AM";
  const h = rawH % 12 === 0 ? 12 : rawH % 12;
  const m = pad(d.getMinutes());
  const timeStr = `${h}:${m} ${period}`;
  if (isToday) return `Today, ${timeStr}`;
  if (isTomorrow) return `Tomorrow, ${timeStr}`;
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${timeStr}`;
}

export function formatShort(value: string): string {
  const d = parseLocalISO(value);
  if (!d) return "";
  const rawH = d.getHours();
  const period = rawH >= 12 ? "PM" : "AM";
  const h = rawH % 12 === 0 ? 12 : rawH % 12;
  const m = pad(d.getMinutes());
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${h}:${m} ${period}`;
}

/**
 * Calendar day (YYYY-MM-DD) an instant falls on, in `timeZone` when given and in
 * the viewer's zone otherwise. Used for the same-day / Today / Tomorrow
 * comparisons so they agree with the times actually rendered — comparing on the
 * device's calendar instead would put "Today" and date labels a day off from the
 * clock beside them whenever the two zones straddle midnight.
 */
function dayKeyFor(iso: string, timeZone?: string | null): string {
  if (timeZone) return localDateIso(iso, timeZone);
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * "Aug 1, 9:00 AM – 5:00 PM" for a booking window.
 *
 * Pass the spot's IANA `timeZone` to render the window on the spot's clock and
 * label it with the zone — a booking is a real-world event at a place, so a host
 * or driver in another zone otherwise reads times that nobody at the spot will
 * experience, and "Today" that isn't today there. Omit it to keep the legacy
 * viewer-local behaviour (unchanged, including the absent zone label).
 */
export function formatDateRange(
  startsAt: string,
  endsAt: string,
  timeZone?: string | null,
): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);

  const zoneOpt = timeZone ? { timeZone } : {};
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...zoneOpt,
  };

  const startTime = start.toLocaleTimeString("en-US", timeOpts);
  const endTime = end.toLocaleTimeString("en-US", timeOpts);

  const startDay = dayKeyFor(startsAt, timeZone);
  const endDay = dayKeyFor(endsAt, timeZone);
  const todayKey = timeZone
    ? localToday(timeZone)
    : dayKeyFor(new Date().toISOString());
  const tomorrowKey = shiftIsoDate(todayKey, { days: 1 });

  const sameDay = startDay === endDay;
  const sameYear = startDay.slice(0, 4) === endDay.slice(0, 4);
  // Only appended when a zone was supplied, so existing callers are unaffected.
  // Resolved per endpoint at its own instant, not at "now": the abbreviation is
  // seasonal, so a January booking read in July must say EST even though the zone
  // is currently on EDT. The times themselves are already correct either way
  // (Intl applies the right offset per instant) — it's only the label that would
  // contradict them, and by exactly the hour a reader would then mis-add.
  //
  // Both endpoints get read because a window can straddle a transition, and one
  // trailing abbreviation then mislabels the far end by an hour. Fall-back night
  // is the case that forces it: 05:30Z–06:30Z in New York is 1:30 AM EDT to
  // 1:30 AM EST, so a single label renders "1:30 AM – 1:30 AM" — an hour-long
  // booking that reads as instantaneous, and unrecoverably so, since a generic
  // "ET" would leave the two ends equally indistinguishable. Naming each side is
  // what makes the duration legible.
  const startAbbrev = timeZone ? zoneAbbrev(timeZone, startsAt) : "";
  const endAbbrev = timeZone ? zoneAbbrev(timeZone, endsAt) : "";
  // Collapsed to one trailing label whenever the two agree, which is every
  // window that doesn't cross a transition — the common case keeps reading
  // "12:30 AM – 8:00 AM EDT" rather than repeating itself.
  const split = startAbbrev !== "" && startAbbrev !== endAbbrev;
  const startZoneLabel = split ? ` ${startAbbrev}` : "";
  const endZoneLabel = endAbbrev ? ` ${endAbbrev}` : "";

  let startLabel: string;
  if (startDay === todayKey) {
    startLabel = "Today";
  } else if (startDay === tomorrowKey) {
    startLabel = "Tomorrow";
  } else {
    startLabel = start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...zoneOpt,
    });
  }

  if (sameDay) {
    return `${startLabel}, ${startTime}${startZoneLabel} – ${endTime}${endZoneLabel}`;
  }

  const endDateLabel = end.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    ...zoneOpt,
  });

  return `${startLabel}, ${startTime}${startZoneLabel} – ${endDateLabel}, ${endTime}${endZoneLabel}`;
}

/**
 * How long a window is, spelled out — "2 hrs 30 min", "1 day 3 hrs".
 *
 * Zone-free by construction: it measures the gap between two instants, which no
 * choice of clock changes. Zero-length windows read "0 min" rather than empty.
 */
export function formatDuration(startsAt: string, endsAt: string): string {
  const diffMins = Math.round(
    (new Date(endsAt).getTime() - new Date(startsAt).getTime()) / 60000,
  );
  const days = Math.floor(diffMins / 1440);
  const hrs = Math.floor((diffMins % 1440) / 60);
  const mins = diffMins % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
  if (hrs > 0) parts.push(`${hrs} hr${hrs > 1 ? "s" : ""}`);
  if (mins > 0) parts.push(`${mins} min`);

  return parts.length === 0 ? "0 min" : parts.join(" ");
}

// ---------------------------------------------------------------------------
// Rounding helpers
// ---------------------------------------------------------------------------

export const SLOT_MINUTES = 15;

/**
 * The one rule for start times: floor to the slot they fall inside. Never
 * nearest, never the next slot. 2:52 is 2:45; at 2:44 the current slot is still
 * 2:30. The prefill, the dropdown floor and the submit gate all derive from
 * this, so they cannot drift apart.
 */
export function floorTo15(d: Date): Date {
  const out = new Date(d);
  out.setMinutes(
    Math.floor(d.getMinutes() / SLOT_MINUTES) * SLOT_MINUTES,
    0,
    0,
  );
  return out;
}

// ---------------------------------------------------------------------------
// Time slot generation
// ---------------------------------------------------------------------------

/** The 96 selectable clock times, one per 15-minute slot. */
export const SLOT_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
  (() => {
    const slots: Array<{ value: string; label: string }> = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += SLOT_MINUTES) {
        const period = h >= 12 ? "PM" : "AM";
        const h12 = h % 12 === 0 ? 12 : h % 12;
        slots.push({
          value: `${pad(h)}:${pad(m)}`,
          label: `${h12}:${pad(m)} ${period}`,
        });
      }
    }
    return slots;
  })();

/** "HH:MM" of the slot `d` falls inside — the key into `SLOT_OPTIONS`. */
export function slotValue(d: Date): string {
  const floored = floorTo15(d);
  return `${pad(floored.getHours())}:${pad(floored.getMinutes())}`;
}

/** The calendar date a bare wall-clock `Date` stands for, as YYYY-MM-DD. */
function dayIsoOf(d: Date): string {
  return isoDate(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * `SLOT_OPTIONS` minus the times that do not exist on `day` at `timeZone`.
 *
 * `SLOT_OPTIONS` is a fixed 96-entry table, so it offers 02:00–02:45 on a
 * spring-forward day even though the destination's clock jumps 02:00 → 03:00 and
 * never reads them. Left in, they are not merely cosmetic: every one of them has
 * to resolve to *some* instant, and whichever way that is decided the window
 * searched is not the window shown — the shift is now forward, so picking 02:30
 * would silently start the search at 03:30. Dropping them is the only outcome
 * where the picker and the resolved instant agree.
 *
 * One transition costs one hour a year in one zone, so on every other day this
 * returns `SLOT_OPTIONS` by identity and callers can keep it in a dependency
 * list. Without a zone nothing is dropped — see `wallClockExistsInZone`.
 */
export function existingSlotOptions(
  day: Date | null,
  timeZone?: string | null,
): typeof SLOT_OPTIONS {
  if (!day || !timeZone) return SLOT_OPTIONS;
  const dayIso = dayIsoOf(day);
  const kept = SLOT_OPTIONS.filter((opt) => {
    const [h, m] = opt.value.split(":").map(Number);
    return wallClockExistsInZone(
      dayIso,
      { hour: h ?? 0, minute: m ?? 0 },
      timeZone,
    );
  });
  return kept.length === SLOT_OPTIONS.length ? SLOT_OPTIONS : kept;
}

/**
 * A bare wall clock moved out of a spring-forward gap, on `timeZone`'s clock.
 *
 * The pickers hand back wall clocks, and a URL can carry any pair of them, so a
 * time the destination's clock skips is reachable even with the picker filtered.
 * Round-tripping it through the resolver — wall clock → instant → wall clock —
 * returns the reading the instant actually has, which for a gap is the
 * forward-shifted one and for every other time is what went in. That keeps the
 * URL, the pill and the query on one wall clock instead of showing 2:30 AM while
 * searching 3:30.
 *
 * Ambiguous fall-back times come back unchanged: both readings are the same wall
 * clock, and which instant it names is `listingWallClockToIso`'s policy, not
 * something a wall clock can express.
 *
 * Caveat, inherent to holding a bare wall clock in a viewer-local `Date`: a time
 * inside the *viewer's* own gap cannot be held at all. `new Date(2026, 2, 8, 2,
 * 30)` on a US machine is already 03:30 before anything here runs, because the
 * constructor normalises it. So on the one day a year the viewer springs forward,
 * a destination time in that same hour arrives here pre-shifted even when the
 * destination reads it fine. The shift is forward either way, so the result stays
 * on the safe side of the policy above; escaping it entirely would mean carrying
 * the window as `{dateIso, minutes}` rather than a `Date`.
 */
function shiftOutOfGap(d: Date, timeZone?: string | null): Date {
  if (!timeZone) return d;
  const iso = listingWallClockToIso(
    dayIsoOf(d),
    { hour: d.getHours(), minute: d.getMinutes() },
    timeZone,
  );
  return wallClockNowIn(new Date(iso), timeZone);
}

// ---------------------------------------------------------------------------
// The destination's clock
// ---------------------------------------------------------------------------

/**
 * "Now" at `timeZone`, as a bare wall clock.
 *
 * The search window is a pair of zoneless wall clocks (`YYYY-MM-DDTHH:MM`) that
 * the driver picks against the *destination*, not against their own device. So
 * every bound derived from "now" — which day the calendar opens on, which slots
 * have already passed, how far back `resolveSearchWindow` lets a start sit — has
 * to be read on that same clock, or the pickers and the resolver disagree about
 * what is in the past. A driver in California searching New York at 8 AM their
 * time is at 11 AM the destination's: 8–11 AM names times that have already gone
 * by where the car would actually be parked.
 *
 * The returned `Date` is a *viewer-local* `Date` carrying the destination's
 * wall-clock fields — the same representation `parseLocalISO` produces, so the
 * two compare directly. It is never a correct instant and must not be read as
 * one; `listingWallClockToIso` is what turns a chosen wall clock into an instant.
 *
 * Without a zone this is just `now`, which is what these screens did throughout
 * and what they still do before a destination is chosen or when a coordinate
 * can't be placed.
 *
 * One seam, shared with `parseLocalISO` and described in full on
 * `shiftOutOfGap`: the constructor below is viewer-local, so a destination wall
 * clock that lands in the *viewer's* spring-forward gap normalises forward an
 * hour and this reads late for about an hour, once a year. It does not require
 * the viewer to be inside the gap themselves — with a viewer in New York on
 * 2026-03-08, a destination on Santiago's clock reading 02:30 comes back as
 * 03:30 while the viewer's own clock says 00:30. Consequence is confined to the
 * floor: "now" reads later than it is, so slots that are genuinely still ahead at
 * the destination are withheld for that hour. That is the direction to be wrong
 * in — reading early would offer a start the backend then rejects as passed — but
 * it is the representation's limit, not a policy, and only `{dateIso, minutes}`
 * in place of a `Date` removes it.
 */
export function wallClockNowIn(now: Date, timeZone?: string | null): Date {
  if (!timeZone) return now;
  // `localDateIso`/`minutesOfDay` fall back to the *ISO string's* own offset when
  // handed no zone, which for the `Z` stamp below is UTC — right for a listing
  // timestamp, wrong for "what time is it where the viewer is sitting". The guard
  // above keeps that fallback out of reach.
  const iso = now.toISOString();
  const [year = 1970, month = 1, day = 1] = localDateIso(iso, timeZone)
    .split("-")
    .map(Number);
  return new Date(year, month - 1, day, 0, minutesOfDay(iso, timeZone));
}

// ---------------------------------------------------------------------------
// Search window resolution
// ---------------------------------------------------------------------------

const DEFAULT_DURATION_MS = 60 * 60_000;
const MIN_DURATION_MS = SLOT_MINUTES * 60_000;
/** Matches the pickers' one-year ceiling, so a URL can't ask for more than the UI offers. */
export const MAX_DURATION_DAYS = 365;
export const MAX_DURATION_MS = MAX_DURATION_DAYS * 86_400_000;

/** Local-ISO ("YYYY-MM-DDTHH:MM") or absolute ISO, as shared links carry. */
function parseFlexible(value: string): Date | null {
  const local = parseLocalISO(value);
  if (local) return local;
  const absolute = new Date(value);
  return Number.isNaN(absolute.getTime()) ? null : absolute;
}

export type SearchWindow = { starts_at: string; ends_at: string };

/**
 * Both ends floor to their slot; a start older than the current slot is pulled
 * up to it; the end is then kept unless that start leaves it under one slot — in
 * which case it becomes start + 1h — or more than a year past it, in which case
 * it is clamped to the ceiling. Idempotent.
 *
 * Every search entry point runs this, so an out-of-range window is unreachable
 * rather than reported — nothing downstream re-validates. The end picker bounds
 * itself at `start + MAX_DURATION_MS`, days and times both, so the clamp only ever
 * fires for a window that arrived in the URL rather than through the UI.
 *
 * `timeZone` is the destination's (see `wallClockNowIn`) — the clock the window's
 * wall clocks were picked on. It moves the past-start floor, which is the one
 * rule here that asks what time it is anywhere: pass it and this floors at the
 * destination's current slot, matching what `DateTimeFields` offers. Omit it and
 * the floor is the viewer's, which is right until a destination is known and
 * silently wrong after — for a California driver searching New York it would pull
 * every start up three hours past what they picked.
 *
 * It also lands both ends on a wall clock the destination's clock actually reads.
 * A wall clock is not self-validating — a spring-forward gap is a property of the
 * zone, not of the string — and this is where a window from a shared link is
 * first seen, so it is the last point at which the URL can still be corrected to
 * the times that will be searched (see `shiftOutOfGap`).
 */
export function resolveSearchWindow(
  raw: Partial<SearchWindow>,
  now: Date = new Date(),
  timeZone?: string | null,
): SearchWindow {
  const earliest = floorTo15(wallClockNowIn(now, timeZone));

  const rawStart = raw.starts_at ? parseFlexible(raw.starts_at) : null;
  const flooredStart = rawStart ? floorTo15(rawStart) : null;
  // Out of the gap before the span math, not after: the duration rules below are
  // measured from the start, so shifting it afterwards would move the end with it
  // and hand back a window an hour off the one just clamped.
  const start = shiftOutOfGap(
    flooredStart && flooredStart >= earliest ? flooredStart : earliest,
    timeZone,
  );

  const rawEnd = raw.ends_at ? parseFlexible(raw.ends_at) : null;
  const end = rawEnd ? floorTo15(rawEnd) : null;
  const span = end ? end.getTime() - start.getTime() : 0;

  let resolvedEnd: Date;
  if (!end || span < MIN_DURATION_MS) {
    resolvedEnd = new Date(start.getTime() + DEFAULT_DURATION_MS);
  } else if (span > MAX_DURATION_MS) {
    resolvedEnd = new Date(start.getTime() + MAX_DURATION_MS);
  } else {
    resolvedEnd = end;
  }

  return {
    starts_at: toLocalISO(start),
    // The end shifts too — the gap is a property of the clock, so an end sitting
    // in one is as unreachable as a start. Shifted last so it is the clamped
    // value being corrected, which can carry it up to an hour past the ceiling:
    // the alternative is an end the destination never reads, and the ceiling is a
    // year-long span where the hour is noise.
    ends_at: toLocalISO(shiftOutOfGap(resolvedEnd, timeZone)),
  };
}
